import React, { useEffect, useRef, useState } from "react";
import {
  ChatBubbleLeftRightIcon,
  MinusIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioPlan } from "../hooks/useStudioPlan";
import sifuImage from "../assets/sifu-boy.jpg";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const OPEN_SIFU_EVENT = "dojotrack:open-sifu";

export function openAskSifu() {
  window.dispatchEvent(new Event(OPEN_SIFU_EVENT));
}

interface AskSifuChatbotProps {
  hidden?: boolean;
}

export default function AskSifuChatbot({ hidden = false }: AskSifuChatbotProps) {
  const { getAuthToken, isAuthenticated } = useAuth();
  const { hasCapability, loading: planLoading } = useStudioPlan();
  const canUseChatbot = hasCapability("aiChatbot");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const latestReplyRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible = isAuthenticated && !planLoading && canUseChatbot && !hidden;

  useEffect(() => {
    const openWidget = () => setOpen(true);
    window.addEventListener(OPEN_SIFU_EVENT, openWidget);
    return () => window.removeEventListener(OPEN_SIFU_EVENT, openWidget);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const container = scrollContainerRef.current;
    const target = latestReplyRef.current;
    if (!container || !target) {
      return;
    }
    const top =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, [chat, loading, open]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading || !canUseChatbot) {
      return;
    }

    setLoading(true);
    setError(null);
    const requestId = ++requestIdRef.current;
    const nextChat: ChatMessage[] = [
      ...chat,
      { role: "user", content: trimmed },
    ];
    setChat(nextChat);
    setInput("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/help`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ messages: nextChat }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (requestId !== requestIdRef.current) {
        return;
      }
      if (!response.ok) {
        throw new Error(data.error || "Failed to get an answer");
      }
      setChat([
        ...nextChat,
        {
          role: "assistant",
          content: data.content || "I could not find an answer for that.",
        },
      ]);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      const message =
        err instanceof Error ? err.message : "Failed to get an answer";
      setError(message);
      setChat([
        ...nextChat,
        {
          role: "assistant",
          content:
            "Sorry, I could not process that. Try again or email support@dojotrack.com.",
        },
      ]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSubmit = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    void sendMessage(input);
  };

  const clearChat = () => {
    requestIdRef.current += 1;
    setChat([]);
    setInput("");
    setError(null);
    setLoading(false);
  };

  if (!visible) {
    return null;
  }

  if (!open) {
    return (
      <button
        type="button"
        className="fixed bottom-4 right-4 z-[10000] flex h-14 w-14 items-center justify-center rounded-full border-2 border-white bg-indigo-600 p-0 shadow-lg transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:border-gray-800"
        onClick={() => setOpen(true)}
        aria-label="Ask Sifu"
      >
        <ChatBubbleLeftRightIcon className="h-7 w-7 text-white" aria-hidden />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[10000] flex w-[min(24rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <img
          src={sifuImage}
          alt=""
          className="h-10 w-10 rounded-full object-cover"
        />
        <p className="min-w-0 flex-1 font-semibold text-gray-900 dark:text-white">
          Ask Sifu
        </p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          title="Minimize"
          aria-label="Minimize Ask Sifu"
        >
          <MinusIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => {
            clearChat();
            setOpen(false);
          }}
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          title="Close"
          aria-label="Close Ask Sifu"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="flex h-[min(28rem,60vh)] flex-col">
        <div
          ref={scrollContainerRef}
          className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4"
        >
          {chat.length === 0 && !loading && (
            <p className="py-6 text-center text-sm text-gray-600 dark:text-gray-300">
              I'm Sifu, a chatbot for DojoTrack how-to questions. What do you want to know?
            </p>
          )}
          {chat.map((message, index) => {
            const isLatestReply =
              !loading &&
              message.role === "assistant" &&
              index ===
                chat.map((item) => item.role).lastIndexOf("assistant");
            return (
              <div
                key={`${message.role}-${index}`}
                ref={isLatestReply ? latestReplyRef : undefined}
                className={`max-w-[90%] whitespace-pre-line rounded-lg px-3 py-2 text-sm ${
                  message.role === "user"
                    ? "ml-auto bg-indigo-100 text-right text-gray-900 dark:bg-indigo-900 dark:text-indigo-50"
                    : "mr-auto bg-gray-100 text-left text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                }`}
              >
                {message.content}
              </div>
            );
          })}
          {loading && (
            <div
              ref={latestReplyRef}
              className="mr-auto max-w-[90%] rounded-lg bg-gray-100 px-3 py-2 text-left text-sm text-gray-400 dark:bg-gray-700"
            >
              Sifu is thinking...
            </div>
          )}
        </div>

        {error && (
          <p className="px-4 pb-2 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        <form
          onSubmit={handleSubmit}
          className="flex gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700"
        >
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Ask a question..."
            disabled={loading}
            autoFocus
            className="flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="inline-flex items-center justify-center rounded bg-indigo-600 px-3 py-2 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
            <span className="sr-only">Send</span>
          </button>
        </form>
      </div>
    </div>
  );
}
